import { activitiesApi } from '@/admin/api/activities';
import type { Appointment } from '@/admin/api/appointments';
import { appointmentsApi } from '@/admin/api/appointments';
import { clientsApi } from '@/admin/api/clients';
import type { Booking } from '@/admin/api/orders';
import { ordersApi } from '@/admin/api/orders';
import { sectionsApi } from '@/admin/api/sections';
import { Route } from '@/admin/routes/_app/clients.$clientId';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { dateI18n } from '@wordpress/date';
import { __ } from '@wordpress/i18n';
import {
	ArrowLeft,
	BookOpen,
	Calendar,
	ChevronDown,
	Clock,
	Eye,
	Mail,
	Pencil,
	Phone,
} from 'lucide-react';
import { useState } from 'react';
import ClientDialog from './client-dialog';

const AVATAR_COLORS = [
	{ bg: 'bg-teal-100', text: 'text-teal-700' },
	{ bg: 'bg-amber-100', text: 'text-amber-700' },
	{ bg: 'bg-red-100', text: 'text-red-600' },
	{ bg: 'bg-blue-100', text: 'text-blue-700' },
	{ bg: 'bg-purple-100', text: 'text-purple-700' },
] as const;

function getAvatarColor(name: string) {
	return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
	return name
		.split(' ')
		.slice(0, 2)
		.map((p) => p[0] ?? '')
		.join('')
		.toUpperCase();
}

function formatTime(t: string): string {
	const [h, m] = t.split(':').map(Number);
	const period = h! >= 12 ? 'PM' : 'AM';
	return `${h! % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
}

function getDurationMinutes(start: string, end: string): number {
	const [sh, sm] = start.split(':').map(Number);
	const [eh, em] = end.split(':').map(Number);
	return eh! * 60 + em! - (sh! * 60 + sm!);
}

function formatApptDate(dateStr: string) {
	const d = new Date(dateStr + 'T00:00:00');
	return {
		month: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
		day: String(d.getDate()),
		weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
	};
}

const BOOKING_PILL: Record<string, { label: string; className: string }> = {
	confirmed: {
		label: __('Active', 'allcoach'),
		className: 'bg-emerald-50 text-emerald-700',
	},
	completed: {
		label: __('Completed', 'allcoach'),
		className: 'bg-green-100 text-green-800',
	},
	pending: {
		label: __('Pending', 'allcoach'),
		className: 'bg-gray-100 text-gray-600',
	},
	cancelled: {
		label: __('Cancelled', 'allcoach'),
		className: 'bg-red-50 text-red-600',
	},
};

const APPT_PILL: Record<string, { label: string; className: string }> = {
	scheduled: {
		label: __('Upcoming', 'allcoach'),
		className: 'bg-sky-50 text-sky-700',
	},
	completed: {
		label: __('Completed', 'allcoach'),
		className: 'bg-green-100 text-green-800',
	},
	cancelled: {
		label: __('Cancelled', 'allcoach'),
		className: 'bg-red-50 text-red-600',
	},
	no_show: {
		label: __('No-show', 'allcoach'),
		className: 'bg-amber-50 text-amber-700',
	},
};

const FALLBACK_PILL = { label: '', className: 'bg-gray-100 text-gray-600' };

type ActivityField = {
	id: string;
	type: string;
	label?: string;
	question?: string;
	options?: Array<{ id: string; label: string }>;
	required?: boolean;
};

function extractFieldsFromContent(
	content: Record<string, unknown> | Array<Record<string, unknown>>,
): ActivityField[] {
	const fields: ActivityField[] = [];
	const items = Array.isArray(content) ? content : [content];

	for (const node of items) {
		if (!node || typeof node !== 'object') continue;

		const nodeType = (node.type as string) || '';
		const attrs = (node.attrs as Record<string, unknown>) || {};

		if (
			[
				'shortAnswer',
				'longAnswer',
				'multipleChoice',
				'checkbox',
				'date',
				'fileUpload',
			].includes(nodeType)
		) {
			fields.push({
				id: (attrs.id as string) || '',
				type: nodeType,
				label: (attrs.label as string) || '',
				question: (attrs.question as string) || (attrs.label as string) || '',
				options: (attrs.options as Array<{ id: string; label: string }>) || [],
				required: (attrs.required as boolean) || false,
			});
		}

		if (node.content && Array.isArray(node.content)) {
			fields.push(...extractFieldsFromContent(node.content));
		}
	}

	return fields;
}

function getFieldTypeLabel(type: string): string {
	const labels: Record<string, string> = {
		shortAnswer: __('Short answer', 'allcoach'),
		longAnswer: __('Long answer', 'allcoach'),
		multipleChoice: __('Multiple choice', 'allcoach'),
		checkbox: __('Checkbox', 'allcoach'),
		date: __('Date', 'allcoach'),
		fileUpload: __('File upload', 'allcoach'),
	};
	return labels[type] || type;
}

function formatFieldValue(
	field: ActivityField,
	value: unknown,
): string | string[] {
	if (value === null || value === undefined) {
		return __('No answer provided', 'allcoach');
	}

	if (field.type === 'multipleChoice' && typeof value === 'string') {
		const option = field.options?.find((opt) => opt.id === value);
		return option?.label || String(value);
	}

	if (field.type === 'checkbox' && Array.isArray(value)) {
		return value
			.map((id) => {
				const option = field.options?.find((opt) => opt.id === id);
				return option?.label || String(id);
			})
			.filter(Boolean);
	}

	if (Array.isArray(value)) {
		return value.join(', ');
	}

	if (typeof value === 'object') {
		return JSON.stringify(value, null, 2);
	}

	return String(value);
}

type EnrichedActivity = {
	id: number;
	title: string;
	sectionTitle: string;
	week: number;
	isCompleted: boolean;
	schema?: {
		version: string;
		content: Record<string, unknown>;
	};
};

export function ClientDetail() {
	const { clientId } = Route.useParams();

	const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
	const [activeTab, setActiveTab] = useState<'sessions' | 'activities'>(
		'sessions',
	);
	const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

	const { data: client } = useSuspenseQuery({
		queryKey: ['client', clientId],
		queryFn: () => clientsApi.get(Number(clientId)),
	});

	const { data: ordersData } = useQuery({
		queryKey: ['orders', { client_id: Number(clientId), per_page: 100 }],
		queryFn: () =>
			ordersApi.list({ client_id: Number(clientId), per_page: 100 }),
	});

	const { data: appointmentsData, isLoading: apptLoading } = useQuery({
		queryKey: ['appointments', { client_id: Number(clientId) }],
		queryFn: () =>
			appointmentsApi.list({ client_id: Number(clientId), per_page: 100 }),
		enabled: selectedBooking !== null,
	});

	const { data: sectionsData, isLoading: sectionsLoading } = useQuery({
		queryKey: ['sections', selectedBooking?.program_id],
		queryFn: () => sectionsApi.list(selectedBooking!.program_id),
		enabled: selectedBooking !== null,
	});

	const { data: completionsData, isLoading: completionsLoading } = useQuery({
		queryKey: [
			'activity-completions',
			selectedBooking?.program_id,
			selectedBooking?.enrollment_id,
		],
		queryFn: () =>
			activitiesApi.getCompletions(
				selectedBooking!.program_id,
				selectedBooking!.enrollment_id,
			),
		enabled: selectedBooking !== null,
	});

	const orders = ordersData?.orders ?? [];

	const programAppointments: Appointment[] = (
		appointmentsData?.session_bookings ?? []
	)
		.filter((a) => a.program?.id === selectedBooking?.program_id)
		.sort(
			(a, b) =>
				new Date(b.scheduled_date).getTime() -
				new Date(a.scheduled_date).getTime(),
		);

	const completedIds = new Set(
		selectedBooking?.activity_progress.completed_activity_ids ?? [],
	);

	// @ts-ignore
	const allActivities: EnrichedActivity[] = (
		sectionsData?.sections ?? []
	).flatMap((s) =>
		s.activities.map((a) => ({
			id: a.id,
			title: (a.settings.title as string | undefined) ?? `Activity ${a.id}`,
			sectionTitle: s.title,
			week: a.week,
			isCompleted: completedIds.has(a.id),
			schema: a.schema,
		})),
	);

	const avatarColor = getAvatarColor(client.name);
	const initials = getInitials(client.name);

	function toggleExpanded(id: number) {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	}

	function selectBooking(booking: Booking) {
		setSelectedBooking(booking);
		setActiveTab('sessions');
		setExpandedIds(new Set());
	}

	function backToOverview() {
		setSelectedBooking(null);
		setExpandedIds(new Set());
	}

	if (selectedBooking) {
		const sp = selectedBooking.session_progress;
		const ap = selectedBooking.activity_progress;
		const progressDone = activeTab === 'sessions' ? sp.completed : ap.completed;
		const progressTotal = activeTab === 'sessions' ? sp.total : ap.total;
		const progressPct =
			progressTotal > 0 ? Math.round((progressDone / progressTotal) * 100) : 0;

		return (
			<div className="mx-auto max-w-[1100px]">
				{/* ← Client name */}
				<button
					onClick={backToOverview}
					className="mb-3.5 flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 font-[inherit] text-[13px] text-gray-500 hover:text-gray-800"
				>
					<ArrowLeft className="size-[13px]" />
					{client.name}
				</button>

				<div className="mb-3.5 border-b border-gray-200 pb-3.5">
					<p className="text-[15px] font-medium text-gray-900">
						{selectedBooking.program_title}
					</p>
				</div>

				<div className="rounded-[10px] border border-gray-200 bg-white p-4">
					<div className="flex gap-1.5">
						{(
							[
								[
									'sessions',
									__('Sessions', 'allcoach'),
									sp.completed,
									sp.total,
								],
								[
									'activities',
									__('Activities', 'allcoach'),
									ap.completed,
									ap.total,
								],
							] as const
						).map(([key, label, done, total]) => (
							<button
								key={key}
								onClick={() => setActiveTab(key)}
								className={cn(
									'cursor-pointer rounded-full border px-3 py-1 font-[inherit] text-[12px] font-medium transition-colors',
									activeTab === key
										? 'border-teal-600 bg-teal-600 text-white'
										: 'border-gray-200 bg-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
								)}
							>
								{label} ({done}/{total})
							</button>
						))}
					</div>

					{/* Progress */}
					<div className="mt-4 mb-4">
						<p className="mb-1.5 text-[13px] text-gray-500">
							{activeTab === 'sessions'
								? `${sp.completed} ${__('of', 'allcoach')} ${sp.total} ${__('sessions completed', 'allcoach')}`
								: `${ap.completed} ${__('of', 'allcoach')} ${ap.total} ${__('activities completed', 'allcoach')}`}
						</p>
						<div className="h-1 w-40 overflow-hidden rounded-full bg-gray-100">
							<div
								className="h-full rounded-full bg-teal-600 transition-all duration-300"
								style={{ width: `${progressPct}%` }}
							/>
						</div>
					</div>

					{/* Sessions */}
					{activeTab === 'sessions' && (
						<>
							{apptLoading ? (
								<div className="flex justify-center py-8">
									<Spinner />
								</div>
							) : programAppointments.length === 0 ? (
								<p className="py-9 text-center text-[13px] text-gray-400">
									{__('No sessions found for this program.', 'allcoach')}
								</p>
							) : (
								<div className="overflow-hidden rounded-lg border border-gray-200">
									{programAppointments.map((appt, i) => {
										const { month, day, weekday } = formatApptDate(
											appt.scheduled_date,
										);
										const duration = getDurationMinutes(
											appt.start_time,
											appt.end_time,
										);
										const pill = APPT_PILL[appt.status] ?? FALLBACK_PILL;
										return (
											<div
												key={appt.id}
												className={cn(
													'flex items-center gap-4 px-5 py-3.5',
													i < programAppointments.length - 1 &&
														'border-b border-gray-100',
												)}
											>
												{/* Calendar block */}
												<div className="w-10 flex-shrink-0 rounded-lg bg-gray-50 px-1 py-1 text-center">
													<div className="text-[9px] font-medium tracking-wider text-gray-400 uppercase">
														{month}
													</div>
													<div className="text-[17px] leading-tight font-medium text-gray-900">
														{day}
													</div>
												</div>

												{/* Description */}
												<p className="flex-1 text-[13px] text-gray-600">
													{weekday}, {formatTime(appt.start_time)}
													{' · '}
													{duration} {__('min', 'allcoach')}
													{appt.meeting_url && ' · Google Meet'}
												</p>

												{/* Status pill */}
												<span
													className={cn(
														'inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
														pill.className,
													)}
												>
													{pill.label}
												</span>
											</div>
										);
									})}
								</div>
							)}
						</>
					)}

					{activeTab === 'activities' && (
						<>
							{sectionsLoading ? (
								<div className="flex justify-center py-8">
									<Spinner />
								</div>
							) : allActivities.length === 0 ? (
								<p className="py-9 text-center text-[13px] text-gray-400">
									{__('No activities found for this program.', 'allcoach')}
								</p>
							) : (
								<div className="overflow-hidden rounded-lg border border-gray-200">
									{allActivities.map((activity, i) => {
										const expanded = expandedIds.has(activity.id);
										const isLast = i === allActivities.length - 1;
										return (
											<div key={activity.id}>
												<div
													className={cn(
														'flex items-center gap-3 px-4 py-3',
														!isLast && !expanded && 'border-b border-gray-100',
														activity.isCompleted &&
															'cursor-pointer hover:bg-gray-50',
													)}
													onClick={() =>
														activity.isCompleted && toggleExpanded(activity.id)
													}
												>
													<div className="flex-1">
														<div className="mb-0.5 text-[13px] font-medium text-gray-900">
															{activity.title}
														</div>
														<div className="text-[12px] text-gray-500">
															{activity.sectionTitle}
															{' · '}
															{__('Week', 'allcoach')} {activity.week}
														</div>
													</div>

													<span
														className={cn(
															'inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
															activity.isCompleted
																? 'bg-green-100 text-green-800'
																: 'bg-gray-100 text-gray-600',
														)}
													>
														{activity.isCompleted
															? __('Completed', 'allcoach')
															: __('Not started', 'allcoach')}
													</span>

													{activity.isCompleted ? (
														<div className="flex items-center gap-1 px-2 py-1 text-[12px] font-medium whitespace-nowrap text-teal-600">
															{__('View responses', 'allcoach')}
															<ChevronDown
																className={cn(
																	'size-[11px] transition-transform duration-150',
																	expanded && 'rotate-180',
																)}
															/>
														</div>
													) : (
														<span className="px-2 py-1 text-[12px] text-gray-400">
															{__('No responses yet', 'allcoach')}
														</span>
													)}
												</div>

												{expanded && (
													<div
														className={cn(
															'mx-4 mb-4 rounded-r-md border-l-[3px] border-teal-600 bg-gray-50 px-4 py-3.5',
															!isLast && 'border-b border-gray-100',
														)}
													>
														{completionsLoading ? (
															<div className="flex justify-center py-2">
																<Spinner />
															</div>
														) : (
															(() => {
																const completion =
																	completionsData?.completions.find(
																		(c) => c.activity_id === activity.id,
																	);
																if (!completion || !completion.response_data) {
																	return (
																		<p className="text-[13px] text-gray-500">
																			{__(
																				'No response data available.',
																				'allcoach',
																			)}
																		</p>
																	);
																}

																// Extract fields from activity schema
																const fields = activity.schema?.content
																	? extractFieldsFromContent(
																			activity.schema.content,
																		)
																	: [];

																if (fields.length === 0) {
																	return (
																		<div className="space-y-3">
																			<div className="mb-2 flex items-center justify-between">
																				<p className="text-[12px] font-medium text-gray-700">
																					{__('Client Response', 'allcoach')}
																				</p>
																				<p className="text-[11px] text-gray-500">
																					{__('Completed', 'allcoach')}{' '}
																					{dateI18n(
																						'M j, Y g:i A',
																						completion.completed_at,
																					)}
																				</p>
																			</div>
																			{Object.entries(
																				completion.response_data,
																			).map(([key, value]) => (
																				<div key={key} className="space-y-1">
																					<p className="text-[12px] font-medium text-gray-600">
																						{key}
																					</p>
																					<div className="rounded-md bg-white px-3 py-2 text-[13px] text-gray-800">
																						{typeof value === 'object'
																							? JSON.stringify(value, null, 2)
																							: String(value)}
																					</div>
																				</div>
																			))}
																		</div>
																	);
																}

																// Count answered fields
																const answeredCount = fields.filter(
																	(f) => completion.response_data[f.id] != null,
																).length;

																// Map responses to fields
																return (
																	<div>
																		{/* Header */}
																		<div className="mb-4 flex items-start justify-between gap-3">
																			<div className="flex-1">
																				<div className="text-[13px] font-medium text-gray-900">
																					{activity.title}
																				</div>
																				<div className="mt-0.5 text-[11px] text-gray-500">
																					{__('Submitted by', 'allcoach')}{' '}
																					{client.name} ·{' '}
																					{dateI18n(
																						'M j, Y',
																						completion.completed_at,
																					)}{' '}
																					{__('at', 'allcoach')}{' '}
																					{dateI18n(
																						'g:i A',
																						completion.completed_at,
																					)}
																				</div>
																			</div>
																			<span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap text-green-800">
																				{answeredCount} {__('of', 'allcoach')}{' '}
																				{fields.length}{' '}
																				{__('answered', 'allcoach')}
																			</span>
																		</div>

																		{/* Questions */}
																		{fields.map((field, idx) => {
																			const value =
																				completion.response_data[field.id];
																			const hasAnswer = value != null;

																			return (
																				<div key={field.id}>
																					{idx > 0 && (
																						<div className="my-3 border-t border-gray-200" />
																					)}
																					<div>
																						<div className="mb-1.5 text-[11px] font-medium text-gray-500">
																							{__('Question', 'allcoach')}{' '}
																							{idx + 1} ·{' '}
																							{getFieldTypeLabel(field.type)}
																						</div>

																						<div className="mb-2 text-[13px] font-medium text-gray-900">
																							{field.question ||
																								field.label ||
																								field.id}
																						</div>

																						{/* Answer */}
																						{field.type === 'multipleChoice' &&
																						field.options &&
																						field.options.length > 0 ? (
																							<div className="mt-1.5 flex flex-col gap-1.5">
																								{field.options.map((option) => {
																									const isSelected =
																										value === option.id;
																									return (
																										<div
																											key={option.id}
																											className={cn(
																												'flex items-center gap-2 text-[13px]',
																												isSelected
																													? 'font-medium text-gray-900'
																													: 'text-gray-500',
																											)}
																										>
																											<div
																												className={cn(
																													'flex size-4 flex-shrink-0 items-center justify-center rounded-full border-2',
																													isSelected
																														? 'border-teal-600 bg-teal-600'
																														: 'border-gray-300 bg-white',
																												)}
																											>
																												{isSelected && (
																													<svg
																														width="10"
																														height="10"
																														fill="none"
																														stroke="#fff"
																														strokeWidth="3"
																														viewBox="0 0 24 24"
																													>
																														<polyline points="20 6 9 17 4 12" />
																													</svg>
																												)}
																											</div>
																											{option.label}
																										</div>
																									);
																								})}
																							</div>
																						) : field.type === 'checkbox' &&
																						  field.options &&
																						  field.options.length > 0 ? (
																							<div className="mt-1.5 flex flex-col gap-1.5">
																								{field.options.map((option) => {
																									const selectedValues =
																										Array.isArray(value)
																											? value
																											: [];
																									const isSelected =
																										selectedValues.includes(
																											option.id,
																										);
																									return (
																										<div
																											key={option.id}
																											className={cn(
																												'flex items-center gap-2 text-[13px]',
																												isSelected
																													? 'font-medium text-gray-900'
																													: 'text-gray-500',
																											)}
																										>
																											<div
																												className={cn(
																													'flex size-4 flex-shrink-0 items-center justify-center rounded border-2',
																													isSelected
																														? 'border-teal-600 bg-teal-600'
																														: 'border-gray-300 bg-white',
																												)}
																											>
																												{isSelected && (
																													<svg
																														width="10"
																														height="10"
																														fill="none"
																														stroke="#fff"
																														strokeWidth="3"
																														viewBox="0 0 24 24"
																													>
																														<polyline points="20 6 9 17 4 12" />
																													</svg>
																												)}
																											</div>
																											{option.label}
																										</div>
																									);
																								})}
																							</div>
																						) : (
																							<div className="rounded-md bg-white px-3 py-2 text-[13px] text-gray-800">
																								{hasAnswer ? (
																									<div className="whitespace-pre-wrap">
																										{formatFieldValue(
																											field,
																											value,
																										)}
																									</div>
																								) : (
																									<div className="text-gray-400 italic">
																										{__(
																											'No answer provided',
																											'allcoach',
																										)}
																									</div>
																								)}
																							</div>
																						)}
																					</div>
																				</div>
																			);
																		})}
																	</div>
																);
															})()
														)}
													</div>
												)}
											</div>
										);
									})}
								</div>
							)}
						</>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-[1100px] p-[22px_28px]">
			{/* @ts-ignore */}
			<Link
				to="/clients"
				className="mb-3.5 flex w-fit items-center gap-1.5 text-[13px] text-gray-500 no-underline hover:text-gray-800"
			>
				<ArrowLeft className="size-[13px]" />
				{__('Clients', 'allcoach')}
			</Link>

			<div className="mb-3.5 flex flex-wrap items-center gap-3.5 rounded-[10px] border border-gray-200 bg-white px-[22px] py-[18px]">
				{/* Avatar */}
				<div
					className={cn(
						'flex size-[50px] flex-shrink-0 items-center justify-center rounded-full text-[17px] font-medium',
						avatarColor.bg,
						avatarColor.text,
					)}
				>
					{initials}
				</div>

				<div className="min-w-0 flex-1">
					<p className="mb-1 text-[17px] font-medium text-gray-900">
						{client.name}
					</p>
					<div className="flex flex-wrap items-center gap-3.5 text-[12px] text-gray-500">
						<span className="flex items-center gap-1">
							<Mail className="size-3 flex-shrink-0" />
							{client.email}
						</span>
						{client.phone && (
							<span className="flex items-center gap-1">
								<Phone className="size-3 flex-shrink-0" />
								{client.phone}
							</span>
						)}
						<span className="flex items-center gap-1">
							<Calendar className="size-3 flex-shrink-0" />
							{__('Joined', 'allcoach')} {dateI18n('F j, Y', client.created_at)}
						</span>
						{client.timezone && (
							<span className="flex items-center gap-1">
								<Clock className="size-3 flex-shrink-0" />
								{client.timezone}
							</span>
						)}
					</div>
				</div>
				<ClientDialog
					trigger={
						<Button
							size="sm"
							className="cursor-pointer hover:bg-gray-50 hover:text-[inherit]"
							variant="outline"
						>
							<Pencil className="size-3" />
							{__('Edit client', 'allcoach')}
						</Button>
					}
					editTarget={client}
				/>
			</div>

			<p className="mb-2.5 text-[13px] font-medium tracking-[0.04em] text-gray-500 uppercase">
				{__('Programs', 'allcoach')} ({orders.length})
			</p>

			<div className="overflow-hidden rounded-[10px] border border-gray-200 bg-white">
				{orders.length === 0 ? (
					<div className="px-[18px] py-9 text-center">
						<div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-[10px] border border-gray-200 bg-gray-50">
							<BookOpen className="size-[18px] text-gray-400" />
						</div>
						<p className="mb-1 text-[13px] font-medium text-gray-800">
							{__('No programs enrolled', 'allcoach')}
						</p>
						<p className="mb-4 text-[12px] text-gray-400">
							{__(
								"This client hasn't been enrolled in any program yet.",
								'allcoach',
							)}
						</p>
					</div>
				) : (
					orders.map((order, i) => {
						const pill = BOOKING_PILL[order.status] ?? FALLBACK_PILL;
						const { session_progress: sp, activity_progress: ap } = order;
						return (
							<div
								key={order.id}
								className={cn(
									'flex items-center gap-3 px-[18px] py-3.5 hover:bg-gray-50',
									i < orders.length - 1 && 'border-b border-gray-100',
								)}
							>
								<div className="flex-1">
									<div className="mb-0.5 text-[13px] font-medium text-gray-900">
										{order.program_title}
									</div>
									<div className="text-[12px] text-gray-500">
										{__('Sessions', 'allcoach')} {sp.completed}/{sp.total}
										{' · '}
										{__('Activities', 'allcoach')} {ap.completed}/{ap.total}
									</div>
								</div>

								<span
									className={cn(
										'inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
										pill.className,
									)}
								>
									{pill.label}
								</span>

								<button
									onClick={() => selectBooking(order)}
									className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 font-[inherit] text-[12px] font-medium text-gray-800 hover:border-gray-300 hover:bg-gray-50"
								>
									<Eye className="size-3" />
									{__('View', 'allcoach')}
								</button>
							</div>
						);
					})
				)}
			</div>
		</div>
	);
}
